import * as React from 'react';
import { ChevronDownIcon, Search as SearchIcon } from 'lucide-react';
import { cn } from '@/shared/utils/styling';
import {
  Button,
  ErrorText,
  Input,
  ItemsListEmptyState,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/shared/components';
import type { Country } from '@/shared/types';
import type { FieldError } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  type PopoverContentProps,
  PopoverPortal,
} from '@radix-ui/react-popover';

type CountryDisplayProperty = 'name' | 'code' | 'phoneCode';

interface CountrySelectInputProps {
  value?: string;
  onValueChange?: (value: string) => void;
  placeholder?: string;
  label?: string;
  className?: string;
  disabled?: boolean;
  countries: Country[];
  error?: FieldError;
  // Configuration for what property to use for the value
  valueProperty?: CountryDisplayProperty;
  // Configuration for what property to display in the button
  displayProperty?: CountryDisplayProperty;
  // Configuration for what property to display in the list items
  listItemDisplayProperty?: CountryDisplayProperty;
  // Configuration for what property to display as secondary info in list items
  listItemSecondaryProperty?: CountryDisplayProperty;
  // Button styling
  buttonClassName?: string;
  // Search placeholder
  searchPlaceholder?: string;
  // Default value
  defaultValue?: Country;
  // Whether to show the Cancel button inside the popover (default: true)
  showCancelButton?: boolean;
  side?: PopoverContentProps['side'];
}

function CountrySelectInput({
  value = '',
  onValueChange,
  className,
  disabled = false,
  countries,
  error,
  valueProperty = 'code',
  displayProperty = 'name',
  listItemDisplayProperty = 'name',
  listItemSecondaryProperty = 'code',
  buttonClassName,
  placeholder,
  searchPlaceholder,
  defaultValue,
  showCancelButton = true,
  side,
}: CountrySelectInputProps) {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const { t } = useTranslation();

  const selectedCountry = React.useMemo(() => {
    return (
      countries.find(country => country[valueProperty] === value) ||
      defaultValue
    );
  }, [value, countries, valueProperty]);

  const filteredCountries = React.useMemo(() => {
    if (!search) return countries;
    return countries.filter(
      country =>
        country.name.toLowerCase().includes(search.toLowerCase()) ||
        country.code.toLowerCase().includes(search.toLowerCase()) ||
        country.phoneCode.toLowerCase().includes(search.toLowerCase())
    );
  }, [countries, search]);

  const handleCountrySelect = (country: Country) => {
    setOpen(false);
    setSearch('');
    onValueChange?.(country[valueProperty]);
  };

  return (
    <div className={cn('flex flex-col', className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            aria-invalid={!!error}
            className={cn(
              'justify-between h-[6rem] min-w-[20rem] bg-secondary-bg rounded-[1.2rem] px-[1.6rem] hover:bg-stroke',
              open && 'bg-stroke',
              buttonClassName
            )}
            disabled={disabled || countries.length === 0}>
            {selectedCountry ? (
              <div className="flex items-center gap-3">
                <img
                  className="w-[2.4rem] h-[2.4rem]"
                  src={selectedCountry.link}
                  alt={selectedCountry.name}
                />
                <span className="p3">{selectedCountry[displayProperty]}</span>
              </div>
            ) : (
              <div className="flex grow items-center justify-between gap-[1.6rem] text-secondary">
                <span className="p3">{placeholder}</span>
                <ChevronDownIcon className="w-[2.4rem] h-[2.4rem]" />
              </div>
            )}
          </Button>
        </PopoverTrigger>

        <PopoverPortal>
          {countries.length > 0 && (
            <PopoverContent
              collisionPadding={32}
              className="w-full md:w-[40rem] p-0 rounded-3xl z-70 max-h-[var(--radix-popper-available-height)] max-w-[85vw]"
              align="start"
              side={side ?? 'bottom'}
              avoidCollisions={true}
              sideOffset={8}>
              <div className="flex flex-col max-h-[var(--radix-popper-available-height)]">
                <div className="flex items-center px-[2.4rem] pt-[2.4rem] pb-[1.6rem] gap-2 flex-shrink-0">
                  <div className="flex items-center gap-2 flex-1">
                    <Input
                      placeholder={searchPlaceholder}
                      showLabel={false}
                      value={search}
                      icon={
                        <SearchIcon className="size-[1.8rem] ml-2 text-tertiary" />
                      }
                      onChange={e => setSearch(e.target.value)}
                      className="rounded-full h-[4.2rem] p3 focus:bg-stroke"
                    />
                  </div>
                  {showCancelButton && (
                    <Button
                      variant="ghost"
                      className="headline5"
                      onClick={() => setOpen(false)}>
                      {t('common.cancel')}
                    </Button>
                  )}
                </div>

                <div
                  tabIndex={0}
                  className="flex-1 overflow-y-auto px-[2.4rem] pb-[2.4rem] focus:outline-none"
                  style={{
                    WebkitOverflowScrolling: 'touch',
                    overscrollBehavior: 'contain',
                  }}
                  onWheel={e => {
                    e.stopPropagation();
                  }}
                  onTouchMove={e => {
                    e.stopPropagation();
                  }}>
                  {filteredCountries.length === 0 ? (
                    <ItemsListEmptyState
                      title={t('common.noResults')}
                      description={t('common.noResultsDescription')}
                      className="py-[3.6rem] px-[0.8rem]"
                    />
                  ) : (
                    filteredCountries.map(country => (
                      <div
                        key={country.code}
                        className="flex items-center justify-between p-3 hover:bg-accent cursor-pointer h-[5.6rem]"
                        onClick={() => handleCountrySelect(country)}>
                        <div className="flex items-center gap-[2.4rem]">
                          <img
                            className="w-[3.6rem] h-[3.6rem]"
                            src={country.link}
                            alt={country.name}
                          />
                          <span className="p3 text-primary">
                            {country[listItemDisplayProperty]}
                          </span>
                        </div>
                        <span className="text-secondary p3">
                          {country[listItemSecondaryProperty]}
                        </span>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </PopoverContent>
          )}
        </PopoverPortal>
      </Popover>
      {error && (
        <ErrorText size="small" className="mt-2">
          {error.message}
        </ErrorText>
      )}
    </div>
  );
}

export { CountrySelectInput };
